import { createStore, createEffect, createApi, createEvent } from "effector";
import { RouteProps } from "react-router-dom";
import { history } from "../App";
import { mainStep } from "./Main";
import { menuStep } from "./Menu";
import { checkoutStep } from "./Checkout";

export type Step = {
  order: number;
  tKey: string; // in steps.json
  validate: (showErrors?: boolean) => Promise<boolean>;
  route: RouteProps;
};
const initialState: (Step & { invalid: boolean })[] = [
  {
    invalid: false,
    ...mainStep,
  },
  {
    invalid: false,
    ...menuStep,
  },
  {
    invalid: false,
    ...checkoutStep,
  },
];
export const $steps = createStore<(Step & { invalid: boolean })[]>(
  initialState
);
export const stepsApi = createApi($steps, {
  setInvalid: (s, { idx, v }: { idx: number; v: boolean }) => {
    const storeCloned = [...s];

    const item = storeCloned[idx];
    if (item) item.invalid = v;

    return storeCloned;
  },
  reset: () => initialState,
});

export const $currentStep = createStore<number>(0);
export const goToStep = createEffect(
  async (v: number): Promise<number | null> => {
    const currentStep = $currentStep.getState();
    const steps = $steps.getState();

    const validationResult = await validateFromTo(currentStep, v);
    const isValidationSuccessful = validationResult === true;

    if (isValidationSuccessful) {
      const targetStep = steps[v];

      history?.push(targetStep.route.path as string);
    } else if (typeof validationResult === "number") {
      const invalidStep = steps[validationResult];

      console.error("Step", invalidStep.tKey, "is invalid!");

      history?.push(invalidStep.route.path as string);
    }

    return isValidationSuccessful ? v : null;
  }
);
$currentStep.on(goToStep.doneData, (s, v) => {
  // if null - validation failed, dont perform step change
  if (v !== null) window.scrollTo(0, 0);
  return v === null ? s : v;
});

const setStep = createEvent<number>("Set_CURRENT_STEP");
$currentStep.on(setStep, (_, v) => v);

// returns "true" if steps valid, or idx of first invalid step
export const validateFromTo = async (
  a: number,
  b: number,
  shouldRedirect = false,
  showErrors = true
): Promise<true | number> => {
  const steps = $steps.getState();

  const stepsFromAToB = steps.slice(a, b);

  let res: true | number = true;
  let idx = a; // this idx should start from idx of first validated step
  for (const step of stepsFromAToB) {
    const isStepValid = await step.validate(showErrors);

    stepsApi.setInvalid({ idx, v: !isStepValid });

    if (!isStepValid) {
      res = idx;
      if (shouldRedirect) {
        history?.push(step.route.path as string);
        setStep(idx);
      }
      break;
    }

    idx++;
  }

  if (res === true) {
    setStep(b);
  }

  return res;
};

export const nextStep = async (): Promise<void> => {
  const currentStep = $currentStep.getState();
  await goToStep(currentStep + 1);
};
export const prevStep = async (): Promise<void> => {
  const currentStep = $currentStep.getState();
  await goToStep(currentStep - 1);
};
